import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';

import type { InvestmentBoardPledgedDto, InvestmentMoveData } from '@/api/pipelines/types';
import { useUnitAllocation } from '@/pages/pipelines/common/hooks/use-unit-allocation';
import { formatReceivedDelta } from '@/pages/pipelines/common/utils/received-amount-delta';
import {
  DatePicker,
  InputMoney,
  isPositiveMoneyValue,
  Label,
  Modal,
  TextArea,
  theme,
  Typography,
} from '@/shared/design-system';
import { formatMoney } from '@/utils/numbers/formatters';

const UNITS_UNDERFLOW_HELPER = 'That price is too high to allocate a hundredth of a unit.';
const UNIT_PRICE_UNDERFLOW_HELPER = 'That many units prices each one below a hundredth.';

interface ConfirmInvestmentDialogProps {
  open: boolean;
  pledged: InvestmentBoardPledgedDto;
  onConfirm: (_data: InvestmentMoveData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

/**
 * Records how much actually arrived when a fund admin approves a subscription by
 * dropping its card on `Invested` — the single move that creates the ledger
 * transaction, and the one the backend rejects without a received amount.
 *
 * The amount is held as the decimal string the API takes, never as a number, so
 * what the admin typed is what the ledger records. The delta beneath the field
 * is the one place numbers appear, and it is a display hint only.
 *
 * The denomination is static: it is always the fund's own, and a wire that
 * arrives in another currency is a support case rather than a board action, so
 * there is deliberately no currency control and no FX here.
 *
 * Units and unit price are two views of the same allocation, kept in step by
 * `useUnitAllocation`: state either one and the amount that arrived fixes the
 * other, so a pair that does not multiply back to the money is not reachable.
 * Only the units are submitted — the price is arithmetic, and the backend takes
 * no field for it.
 */
export const ConfirmInvestmentDialog = ({
  open,
  pledged,
  onConfirm,
  onCancel,
  isSubmitting = false,
}: ConfirmInvestmentDialogProps) => {
  const allocation = useUnitAllocation(pledged.amount);
  const { received, units, unitPrice } = allocation;
  const [date, setDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');

  // Re-read the pledged figure whenever the dialog opens, so approving a second
  // card never inherits the amount typed for the first. `pledged` is
  // intentionally not a dependency: a background board refetch mid-edit would
  // otherwise wipe what the admin has typed.
  useEffect(() => {
    if (!open) return;
    allocation.reset(pledged.amount);
    setDate(null);
    setNotes('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isValid = allocation.isComplete;
  const pledgedAmount = Number(pledged.amount);
  const delta = formatReceivedDelta(received, pledged.amount, pledged.denomination);
  // Only a rounded-to-nothing figure is worth a message; an empty received amount
  // already blocks Confirm on its own and is not a unit problem.
  const hasReceived = isPositiveMoneyValue(received);
  const unitsUnderflow = hasReceived && isPositiveMoneyValue(unitPrice) && units === '0.00';
  const unitPriceUnderflow = hasReceived && isPositiveMoneyValue(units) && unitPrice === '0.00';

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      title="Confirm investment"
      description="Record how much arrived and how many units were allocated. This creates the ledger transactions."
      maxWidth={420}
      secondaryAction={{ label: 'Cancel', onClick: onCancel }}
      primaryAction={{
        label: 'Confirm',
        disabled: !isValid || isSubmitting,
        onClick: () => {
          if (!isValid || isSubmitting) return;
          onConfirm({
            receivedAmount: received,
            denomination: pledged.denomination,
            units,
            ...(date ? { date: date.toISOString() } : {}),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          });
        },
      }}
    >
      <Stack flexDirection="column" gap="16px">
        <Stack flexDirection="row" justifyContent="space-between" alignItems="baseline" gap="12px">
          <Typography variant="body3" color={theme.colors.text.secondary}>
            Pledged
          </Typography>
          <Typography variant="body2" color={theme.colors.text.primary}>
            {`${formatMoney(pledgedAmount, pledged.denomination)} ${pledged.denomination}`}
          </Typography>
        </Stack>

        <Stack flexDirection="column" gap="4px">
          <Stack flexDirection="row" alignItems="flex-end" gap="8px">
            <InputMoney
              label="Received"
              placeholder="0"
              value={received}
              onValueChange={allocation.setReceived}
              disabled={isSubmitting}
            />
            <Typography variant="body2" color={theme.colors.text.secondary}>
              {pledged.denomination}
            </Typography>
          </Stack>

          {delta && (
            <Typography variant="body4" color={theme.colors.text.secondary}>
              {delta}
            </Typography>
          )}
        </Stack>

        <InputMoney
          label="Units"
          placeholder="0"
          value={units}
          onValueChange={allocation.setUnits}
          disabled={isSubmitting}
          state={unitsUnderflow ? 'destructive' : 'default'}
          helperText={unitsUnderflow ? UNITS_UNDERFLOW_HELPER : undefined}
        />

        <Stack flexDirection="row" alignItems="flex-end" gap="8px">
          <InputMoney
            label="Unit price"
            placeholder="0"
            value={unitPrice}
            onValueChange={allocation.setUnitPrice}
            disabled={isSubmitting}
            state={unitPriceUnderflow ? 'destructive' : 'default'}
            helperText={unitPriceUnderflow ? UNIT_PRICE_UNDERFLOW_HELPER : undefined}
          />
          <Typography variant="body2" color={theme.colors.text.secondary}>
            {pledged.denomination}
          </Typography>
        </Stack>

        <Stack flexDirection="column" gap="4px">
          <Label>Investment date</Label>
          <DatePicker
            fullWidth
            placeholder="Select date"
            value={date}
            onChange={setDate}
            maxDate={new Date()}
            disabled={isSubmitting}
          />
        </Stack>

        <TextArea
          label="Notes"
          placeholder="Add any notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          maxLength={5000}
          disabled={isSubmitting}
        />
      </Stack>
    </Modal>
  );
};
